import { useMemo } from "react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Clock, MapPin, Plus, ExternalLink, CalendarDays, Pencil } from "lucide-react";

const DIAS_SEMANA = ["D", "S", "T", "Q", "Q", "S", "S"];
const TIPO_LABEL = { evento: "Evento", inscricao: "Inscrição", programacao: "Programação" };

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export const CalendarGrid = ({ cursor, dias, selected, onSelect, loading }) => {
  const cells = useMemo(() => {
    const y = cursor.getFullYear(), m = cursor.getMonth();
    const first = new Date(y, m, 1).getDay();
    const total = new Date(y, m + 1, 0).getDate();
    const arr = Array.from({ length: first }, () => null);
    for (let d = 1; d <= total; d++) arr.push(`${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`);
    return arr;
  }, [cursor]);
  const hoje = todayISO();

  return (
    <div className={`px-3 pb-2 transition-opacity ${loading ? "opacity-50" : ""}`}>
      <div className="grid grid-cols-7 mb-1">
        {DIAS_SEMANA.map((d, i) => <div key={i} className="text-center text-[10px] font-medium text-muted-foreground py-1">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-y-1">
        {cells.map((iso, i) => {
          if (!iso) return <div key={`e${i}`} />;
          const items = dias[iso] || [];
          const has = items.length > 0;
          const isSel = iso === selected;
          const isToday = iso === hoje;
          const cor = has ? items[0].cor : undefined;
          return (
            <button
              key={iso}
              type="button"
              onClick={() => onSelect(iso)}
              className="flex flex-col items-center justify-start h-11 rounded-xl transition-colors hover:bg-muted/60"
              data-testid={`calendar-day-${iso}`}
            >
              <span
                className={`h-7 w-7 flex items-center justify-center rounded-full text-sm font-medium transition-all ${isSel ? "ring-2 ring-offset-1 ring-primary" : ""} ${has ? "text-white shadow-sm" : isToday ? "text-primary font-bold" : "text-foreground"}`}
                style={has ? { backgroundColor: cor } : undefined}
              >
                {parseInt(iso.slice(-2), 10)}
              </span>
              {has && (
                <span className="flex gap-0.5 mt-0.5">
                  {items.slice(0, 3).map((it, k) => <span key={k} className="h-1 w-1 rounded-full" style={{ backgroundColor: it.cor }} />)}
                  {items.length > 3 && <span className="text-[8px] leading-none text-muted-foreground">+{items.length - 3}</span>}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export const DaySummary = ({ selected, items, podeCriar, podeEditar, onCreate, onEdit, onOpenLink }) => {
  const [y, m, d] = selected.split("-");
  const label = new Date(+y, +m - 1, +d).toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long" });
  return (
    <div className="border-t bg-muted/20 px-4 py-3 space-y-2" data-testid="calendar-day-summary">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-semibold capitalize text-muted-foreground">{label}</p>
        {podeCriar && (
          <Button size="sm" variant="outline" className="h-7 text-xs rounded-full" onClick={onCreate} data-testid="calendar-create-event">
            <Plus className="h-3.5 w-3.5 mr-1" /> Evento neste dia
          </Button>
        )}
      </div>
      {items.length === 0 ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground py-1">
          <CalendarDays className="h-3.5 w-3.5" /> Nenhuma atividade programada
        </div>
      ) : items.map(it => (
        <div
          key={`${it.tipo}-${it.id}`}
          role={it.link ? "button" : undefined}
          onClick={() => onOpenLink(it.link)}
          className={`w-full text-left flex items-start gap-3 p-2.5 rounded-xl bg-card border transition-colors ${it.link ? "hover:border-primary/40 cursor-pointer" : "cursor-default"}`}
          data-testid={`calendar-item-${it.id}`}
        >
          <span className="w-1 self-stretch rounded-full shrink-0" style={{ backgroundColor: it.cor }} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium truncate">{it.titulo}</p>
              <Badge variant="outline" className="text-[9px] px-1.5 py-0 shrink-0" style={{ borderColor: it.cor, color: it.cor }}>{TIPO_LABEL[it.tipo] || it.tipo}</Badge>
            </div>
            <div className="flex flex-wrap gap-x-3 text-[11px] text-muted-foreground mt-0.5">
              {it.horario && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{it.horario}</span>}
              {it.local && <span className="flex items-center gap-1 min-w-0"><MapPin className="h-3 w-3 shrink-0" /><span className="truncate">{it.local}</span></span>}
            </div>
            {it.descricao && <p className="text-[11px] text-muted-foreground mt-1 line-clamp-2">{it.descricao}</p>}
          </div>
          {podeEditar && it.tipo === "evento" && (
            <Button
              size="icon" variant="ghost" className="h-8 w-8 shrink-0 -mr-1 text-muted-foreground hover:text-primary"
              title="Editar evento"
              onClick={(e) => { e.stopPropagation(); onEdit(it); }}
              data-testid={`calendar-edit-${it.id}`}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          )}
          {it.link && <ExternalLink className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-1" />}
        </div>
      ))}
    </div>
  );
};
